import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import apiClient from '../utils/api';

const PLANS = [
  {
    key: 'free', name: 'Free', price: '$0', period: '/mo',
    features: ['5 invoices/month', 'Basic dashboard', 'PDF download'],
    color: '#64748b', gradient: 'linear-gradient(135deg, #f1f5f9, #e2e8f0)',
  },
  {
    key: 'freelancer', name: 'Freelancer', price: '$15', period: '/mo',
    features: ['50 invoices/month', 'Basic reports', 'Email support', 'Client portal'],
    color: '#6366f1', gradient: 'linear-gradient(135deg, #eef2ff, #e0e7ff)', popular: false,
  },
  {
    key: 'business', name: 'Business', price: '$49', period: '/mo',
    features: ['Unlimited invoices', 'Payment reminders', 'Priority support', 'Advanced reports', 'Team access'],
    color: '#8b5cf6', gradient: 'linear-gradient(135deg, #f5f3ff, #ede9fe)', popular: true,
  },
  {
    key: 'plus', name: 'Plus', price: '$99', period: '/mo',
    features: ['Everything in Business', 'Multi-currency', 'API access', 'White-label', 'Dedicated support'],
    color: '#ec4899', gradient: 'linear-gradient(135deg, #fdf2f8, #fce7f3)',
  },
];


// ─── Main Settings Component ─────────────────────────────────────────────────
const Settings = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [stripeStatus, setStripeStatus] = useState({ connected: false });
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [disconnecting, setDisconnecting] = useState(false);
  const [purchasingPlan, setPurchasingPlan] = useState(null);
  const [openingPortal, setOpeningPortal] = useState(false);
  const [notification, setNotification] = useState(null);


  useEffect(() => {
    fetchSettings();
    handleUrlParams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUrlParams = async () => {
    const stripeParam = searchParams.get('stripe');
    const subParam = searchParams.get('subscription');
    const planParam = searchParams.get('plan');
    const sessionId = searchParams.get('session_id');

    const actionParam = searchParams.get('action');

    if (stripeParam === 'connected') {
      showNotification('success', '🎉 Stripe connected successfully! You can now accept online payments.');
    } else if (stripeParam === 'error') {
      showNotification('error', 'Failed to connect Stripe. Please try again.');
    } else if (stripeParam === 'refresh') {
      showNotification('info', 'Please complete the Stripe onboarding to activate your account.');
    }

    if (actionParam === 'connect-stripe') {
      handleConnect();
    }

    if (subParam === 'success') {
      showNotification('success', `🎉 You're now on the ${planParam ? planParam.charAt(0).toUpperCase() + planParam.slice(1) : ''} plan!`);
      if (sessionId && planParam) {
        try {
          await apiClient.post('/settings/subscription/verify', { session_id: sessionId, plan: planParam });
          fetchSettings();
        } catch (e) { /* Already handled by webhook */ }
      }
    } else if (subParam === 'cancelled') {
      showNotification('info', 'Plan upgrade was cancelled. No charge was made.');
    } else if (subParam === 'portal_return') {
      // User just returned from the Stripe billing portal (may have cancelled their plan)
      // Refresh subscription data — webhook will have already updated tier to 'free' if cancelled
      await fetchSettings();
      // Check the refreshed subscription tier
      try {
        const subRes = await apiClient.get('/settings/subscription');
        const tier = subRes.data?.tier || 'free';
        const status = subRes.data?.subscription_status;
        if (tier === 'free' || status === 'cancelled') {
          showNotification('info', 'Your subscription has been cancelled. You are now on the Free plan.');
        } else {
          showNotification('success', 'Welcome back! Your subscription is still active.');
        }
        setSubscription(subRes.data);
      } catch (e) { /* silently ignore */ }
    }

    if (stripeParam || subParam) setSearchParams({});
  };

  const fetchSettings = async () => {
    try {
      const [stripeRes, subRes] = await Promise.all([
        apiClient.get('/stripe/status'),
        apiClient.get('/settings/subscription'),
      ]);
      setStripeStatus(stripeRes.data);
      setSubscription(subRes.data);
    } catch (err) {
      console.error('Error fetching settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const showNotification = (type, message, duration = 5000) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), duration);
  };

  const handleConnect = async () => {
    setLoading(true);
    try {
      const res = await apiClient.post('/stripe/connect/start-onboarding');
      if (res.data.url) {
        window.location.href = res.data.url;
      } else {
        showNotification('error', 'Failed to generate onboarding link.');
      }
    } catch (err) {
      showNotification('error', err.response?.data?.detail || 'Failed to start Stripe connection.');
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect your Stripe account? Payment links will stop working.')) return;
    setDisconnecting(true);
    try {
      await apiClient.post('/stripe/disconnect');
      setStripeStatus({ connected: false });
      showNotification('info', 'Stripe account disconnected.');
    } catch (err) {
      showNotification('error', 'Failed to disconnect. Please try again.');
    } finally {
      setDisconnecting(false);
    }
  };

  const purchasePlan = async (planKey) => {
    if (planKey === 'free') return;
    setPurchasingPlan(planKey);
    try {
      const res = await apiClient.post('/settings/subscription/checkout', { plan: planKey });
      window.location.href = res.data.url;
    } catch (err) {
      showNotification('error', err.response?.data?.detail || 'Failed to start checkout. Please try again.');
      setPurchasingPlan(null);
    }
  };

  const openBillingPortal = async () => {
    setOpeningPortal(true);
    try {
      const res = await apiClient.post('/settings/subscription/portal');
      window.open(res.data.url, '_blank');
    } catch (err) {
      showNotification('error', err.response?.data?.detail || 'Could not open billing portal.');
    } finally {
      setOpeningPortal(false);
    }
  };

  const currentPlan = PLANS.find(p => p.key === (subscription?.tier || 'free'));
  const hasActiveSub = subscription?.subscription_status === 'active';

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="settings-loading">
        <div className="w-8 h-8 rounded-full border-2 border-indigo-500 border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="settings" className="max-w-4xl">

      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-900" data-testid="settings-title">Settings</h1>
        <p className="text-slate-500 text-sm mt-1">Manage your Stripe connection and subscription plan</p>
      </div>

      {/* Notification banner */}
      {notification && (
        <div className={`mb-6 p-4 rounded-2xl flex items-center justify-between ${notification.type === 'success' ? 'bg-green-50 border border-green-200 text-green-800' :
          notification.type === 'error' ? 'bg-red-50 border border-red-200 text-red-800' :
            'bg-blue-50 border border-blue-200 text-blue-800'}`}>
          <span className="text-sm font-medium">{notification.message}</span>
          <button onClick={() => setNotification(null)} className="text-lg leading-none opacity-60 hover:opacity-100">×</button>
        </div>
      )}

      {/* ── Stripe Connect Card ── */}
      <div className="card p-6 mb-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-4 flex-1 min-w-0">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center text-2xl flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, #635bff22, #635bff11)' }}>💳</div>
            <div className="min-w-0">
              <h2 className="text-base font-semibold text-slate-900" data-testid="stripe-section-title">
                Stripe Connect
              </h2>
              <p className="text-sm text-slate-500 mt-0.5">
                Connect your Stripe account to accept online payments from clients
              </p>

              {stripeStatus.connected ? (
                <div className="flex items-center gap-3 mt-3 flex-wrap" data-testid="stripe-connected">
                  <span className="flex items-center gap-1.5 text-sm font-semibold text-green-600">
                    <span className="w-2 h-2 rounded-full bg-green-500 inline-block animate-pulse" />
                    Connected
                  </span>
                  {stripeStatus.account_id && (
                    <span className="text-xs text-slate-400 font-mono bg-slate-50 px-2 py-0.5 rounded-lg border">
                      {stripeStatus.account_id}
                    </span>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-1.5 mt-3 text-sm font-medium text-slate-400" data-testid="stripe-not-connected">
                  <span className="w-2 h-2 rounded-full bg-slate-300 inline-block" />
                  Not connected
                </div>
              )}
            </div>
          </div>

          <div className="flex-shrink-0 flex flex-col gap-2 items-end">
            {stripeStatus.connected ? (
              <>
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-semibold"
                  style={{ background: '#dcfce7', color: '#15803d' }}>
                  ✓ Active
                </div>
                <button
                  onClick={handleDisconnect}
                  disabled={disconnecting}
                  data-testid="disconnect-stripe-button"
                  className="text-xs text-slate-400 hover:text-red-500 transition-colors px-2 py-1">
                  {disconnecting ? 'Disconnecting...' : 'Disconnect'}
                </button>
              </>
            ) : (
              <button
                onClick={handleConnect}
                disabled={loading}
                data-testid="connect-stripe-settings-button"
                className="btn-primary flex items-center gap-2 text-sm disabled:opacity-50">
                {loading ? 'Connecting...' : '💳 Connect Stripe'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Subscription Plans ── */}
      <div className="card p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-base font-semibold text-slate-900" data-testid="subscription-section-title">
              Subscription Plan
            </h2>
            <p className="text-sm text-slate-500 mt-0.5">
              Current: <span className="font-semibold text-slate-900" data-testid="current-plan">
                {currentPlan?.name || 'Free'}
              </span>
              {hasActiveSub && (
                <span className="ml-2 px-2 py-0.5 text-xs rounded-full font-medium bg-green-100 text-green-700">Active</span>
              )}
            </p>
          </div>
          {hasActiveSub && subscription?.stripe_customer_id && (
            <button onClick={openBillingPortal} disabled={openingPortal}
              className="text-sm font-medium px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors">
              {openingPortal ? 'Opening...' : '⚙️ Manage Billing'}
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {PLANS.map((plan) => {
            const isCurrent = (subscription?.tier || 'free') === plan.key;
            const isLoading = purchasingPlan === plan.key;
            return (
              <div key={plan.key}
                className={`relative rounded-2xl p-5 border-2 transition-all ${isCurrent ? 'border-indigo-400 shadow-md' : 'border-slate-100 hover:border-slate-200'}`}
                style={{ background: plan.gradient }}
                data-testid={`plan-card-${plan.key}`}>

                {plan.popular && !isCurrent && (
                  <div className="absolute -top-3 left-5">
                    <span className="px-3 py-1 text-xs font-bold rounded-full text-white"
                      style={{ background: 'linear-gradient(135deg, #8b5cf6, #6366f1)' }}>Most Popular</span>
                  </div>
                )}
                {isCurrent && (
                  <div className="absolute -top-3 right-5">
                    <span className="px-3 py-1 text-xs font-bold rounded-full text-white"
                      style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}>✓ Current Plan</span>
                  </div>
                )}

                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="font-bold text-slate-900 text-lg">{plan.name}</h3>
                    <p className="text-slate-500 text-xs mt-0.5">{plan.key === 'free' ? 'Forever free' : 'Per month, billed monthly'}</p>
                  </div>
                  <div className="text-right">
                    <span className="text-2xl font-bold" style={{ color: plan.color }}>{plan.price}</span>
                    <span className="text-xs text-slate-400">{plan.period}</span>
                  </div>
                </div>

                <ul className="space-y-2 mb-5">
                  {plan.features.map((f, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-slate-700">
                      <span className="text-xs" style={{ color: plan.color }}>✓</span> {f}
                    </li>
                  ))}
                </ul>

                {plan.key === 'free' ? (
                  <div className="w-full py-2 text-center text-sm font-medium rounded-xl text-slate-400">
                    {isCurrent ? 'Your current plan' : 'Free tier'}
                  </div>
                ) : isCurrent ? (
                  <div className="w-full py-2 text-center text-sm font-semibold rounded-xl"
                    style={{ background: 'rgba(99,102,241,0.12)', color: '#6366f1' }}>✓ Active</div>
                ) : (
                  <button onClick={() => purchasePlan(plan.key)} disabled={!!purchasingPlan}
                    data-testid={`upgrade-plan-${plan.key}`}
                    className="w-full py-2 text-sm font-semibold rounded-xl text-white transition-all disabled:opacity-50"
                    style={{ background: `linear-gradient(135deg, ${plan.color}, ${plan.color}dd)` }}>
                    {isLoading ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="animate-spin w-3.5 h-3.5" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        Redirecting to Stripe...
                      </span>
                    ) : `Upgrade to ${plan.name} →`}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        <p className="text-xs text-slate-400 text-center mt-5">
          🔒 Payments are securely processed by Stripe. Cancel anytime.
        </p>
      </div>
    </div>
  );
};

export default Settings;